import type { ComponentPropsWithoutRef } from 'react';
import { MermaidChart } from './MermaidChart';

type HeadingProps = ComponentPropsWithoutRef<'h2'>;
type ParagraphProps = ComponentPropsWithoutRef<'p'>;
type AnchorProps = ComponentPropsWithoutRef<'a'>;
type ListProps = ComponentPropsWithoutRef<'ul'>;
type BlockquoteProps = ComponentPropsWithoutRef<'blockquote'>;
type PreProps = ComponentPropsWithoutRef<'pre'>;
type CodeProps = ComponentPropsWithoutRef<'code'>;

// Custom MDX component map — replaces default HTML elements with styled versions.
// Max-width and padding are set by the parent container, not here.

export const mdxComponents = {
  h2: ({ children, ...props }: HeadingProps) => (
    <h2
      className="text-2xl md:text-[1.65rem] font-bold text-slate-900 mt-12 mb-5 tracking-tight leading-snug"
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: HeadingProps) => (
    <h3
      className="text-xl font-semibold text-slate-900 mt-10 mb-4 tracking-tight leading-snug"
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }: HeadingProps) => (
    <h4
      className="text-lg font-semibold text-slate-900 mt-8 mb-3"
      {...props}
    >
      {children}
    </h4>
  ),
  p: ({ children, ...props }: ParagraphProps) => (
    <p
      className="text-[1.0625rem] md:text-lg text-slate-700 leading-[1.8] mb-6"
      {...props}
    >
      {children}
    </p>
  ),
  ul: ({ children, ...props }: ListProps) => (
    <ul
      className="list-disc list-outside pl-6 mb-6 space-y-2 text-[1.0625rem] md:text-lg text-slate-700 leading-[1.5]"
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: ComponentPropsWithoutRef<'ol'>) => (
    <ol
      className="list-decimal list-outside pl-6 mb-6 space-y-2 text-[1.0625rem] md:text-lg text-slate-700 leading-[1.5]"
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }: ComponentPropsWithoutRef<'li'>) => (
    <li className="pl-1" {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }: BlockquoteProps) => (
    <blockquote
      className="border-l-4 border-[#004f4f] pl-6 py-1 my-8 text-slate-600 italic text-lg leading-relaxed"
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: () => (
    <hr className="border-0 border-t border-gray-200 my-12" />
  ),
  strong: ({ children, ...props }: ComponentPropsWithoutRef<'strong'>) => (
    <strong className="font-semibold text-slate-900" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }: ComponentPropsWithoutRef<'em'>) => (
    <em className="italic text-slate-700" {...props}>
      {children}
    </em>
  ),
  a: ({ children, href, ...props }: AnchorProps) => (
    <a
      href={href}
      className="text-[#004f4f] underline decoration-2 underline-offset-4 hover:text-[#003d3d] focus:outline-none focus:ring-2 focus:ring-[#004f4f] rounded transition-colors"
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      {...props}
    >
      {children}
    </a>
  ),
  code: ({ children, ...props }: CodeProps) => (
    <code
      className="bg-gray-100 text-slate-800 px-1.5 py-0.5 rounded text-[0.875em] font-mono"
      {...props}
    >
      {children}
    </code>
  ),
  pre: ({ children, ...props }: PreProps) => (
    <pre
      className="bg-slate-900 text-gray-100 rounded-xl p-6 overflow-x-auto mb-6 text-sm font-mono leading-relaxed"
      {...props}
    >
      {children}
    </pre>
  ),
  MermaidChart,
};
